// Sveobuhvatni SEO composable za Pijace.
// Iznutra zove Nuxt-ove useSeoMeta() (za meta tagove) i useHead() (za canonical link,
// JSON-LD script blokove i htmlAttrs).
//
// Postavlja: title, description, apple-mobile-web-app-title,
// OG (type, title, description, url, image, site_name, locale),
// Twitter Card (card, title, description, image),
// <link rel="canonical">,
// JSON-LD <script type="application/ld+json"> blokove,
// <html> atribute.
//
// Logika:
// - og:image se uvek normalizuje na apsolutni URL (Facebook trazi pun URL)
// - og:locale se mapira iz 'sr'/'en' u 'sr_RS'/'en_US'
// - ako image nije zadat, koristi se fallback /icon-384x384.png
// - canonical default = url
// - jsonLd niz se filtrira: null/undefined item-i se preskaču
// - sve vrednosti mogu biti string, ref, computed ili getter (preko toValue)
//
// SSR napomena:
// useSeoMeta se MORA pozvati sa objektom u kojem je svaka stavka getter,
// a NE sa jednim getter-om koji vraca ceo objekat. U Nuxt 3.13 (@unhead/vue)
// drugi oblik ne ubacuje meta tagove u SSR HTML — server vraca default
// vrednosti iz nuxt.config, a tek nakon hydration-a se na klijentu
// postave prave vrednosti (sto kvari SEO i social share preview).

import type { MaybeRefOrGetter } from 'vue'

interface PijaceSeoInput {
  /** <title> + og:title + twitter:title */
  title: MaybeRefOrGetter<string>

  /** meta description + og:description + twitter:description */
  description: MaybeRefOrGetter<string>

  /** canonical URL — postavlja se kao og:url (i kao <link rel="canonical"> ako canonical nije eksplicitno zadat) */
  url: MaybeRefOrGetter<string>

  /** og:type (default: 'website'). Primeri: 'article', 'video.other', 'profile' */
  type?: MaybeRefOrGetter<string>

  /** og:image i twitter:image — apsolutni URL ili relativni put sa korenom (/...) */
  image?: MaybeRefOrGetter<string | null | undefined>

  /** jezik: 'sr' ili 'en' (mapira se na 'sr_RS' / 'en_US'); podrazumevano 'sr' */
  locale?: MaybeRefOrGetter<string>

  /** <link rel="canonical"> URL. Ako nije zadat, koristi se `url`. */
  canonical?: MaybeRefOrGetter<string>

  /** Zadrzava query string u canonical/og:url (npr. ?q= na stranici pretrage oglasa); default: uklanja se */
  keepQuery?: boolean

  /** Niz JSON-LD objekata. Svaki postaje <script type="application/ld+json">. Null/undefined se filtrira. */
  jsonLd?: MaybeRefOrGetter<Array<Record<string, any> | null | undefined>>

  /** <html> atributi (npr. { lang: 'sr-RS' }) */
  htmlAttrs?: MaybeRefOrGetter<Record<string, any>>
}

const SITE_NAME = 'Pijace.com'
const SITE_URL = 'https://pijace.com'
const FALLBACK_IMAGE = `${SITE_URL}/1200x630.png`

function toOgLocale(loc: string): string {
  if (loc === 'en') return 'en_US'
  if (loc === 'sr' || !loc) return 'sr_RS'

  return loc
}

function toAbsoluteImage(img: string | null | undefined): string {
  if (!img) return FALLBACK_IMAGE
  if (img.startsWith('http')) return img
  if (img.startsWith('/')) return `${SITE_URL}${img}`

  return `${SITE_URL}/${img}`
}

// canonical bez query stringa (utm, fbclid, filteri) i bez kose crte na kraju,
// da svaka stranica ima jedan kanonski URL; putanju velikim/malim slovima sredjuje server/middleware/canonical-url.ts
function toCanonical(url: string | null | undefined, keepQuery = false): string {
  if (!url) return ''

  // hash se uvek uklanja; query samo kad stranica to trazi (keepQuery)
  return (keepQuery ? url.replace(/#.*$/, '') : url.replace(/[?#].*$/, '')).replace(/(?<=[^/:])\/+$/, '')
}

export function usePijaceSeo(input: PijaceSeoInput) {
  // 1) Meta tagovi (title, description, OG, Twitter)
  //
  // VAŽNO: koristimo per-property getter pattern, NE function-form koji vraća
  // ceo objekat. Tj. ovako:
  //   useSeoMeta({ title: () => x, description: () => y, ... })
  // a NE ovako:
  //   useSeoMeta(() => ({ title, description, ... }))
  //
  // Drugi oblik nije pouzdan tokom SSR-a u @unhead/vue verziji koju koristi
  // Nuxt 3.13 — tagovi se ne ubacuju u SSR HTML, pa server vraca default
  // vrednosti iz nuxt.config (title, og:title, og:url itd. ostaju neizmenjeni).
  // Per-property getter-i se uredno serijalizuju u SSR HTML.
  useSeoMeta({
    title: () => toValue(input.title) || '',
    description: () => toValue(input.description) || '',
    appleMobileWebAppTitle: () => toValue(input.title) || '',

    ogType: () => toValue(input.type) || 'website',
    ogTitle: () => toValue(input.title) || '',
    ogDescription: () => toValue(input.description) || '',
    ogUrl: () => toCanonical(toValue(input.url), toValue(input.keepQuery)),
    ogImage: () => toAbsoluteImage(toValue(input.image)),
    ogSiteName: SITE_NAME,
    ogLocale: () => toOgLocale(toValue(input.locale) || 'sr'),

    twitterCard: 'summary_large_image',
    twitterTitle: () => toValue(input.title) || '',
    twitterDescription: () => toValue(input.description) || '',
    twitterImage: () => toAbsoluteImage(toValue(input.image)),
  })

  // 2) Canonical link, JSON-LD script blokovi, htmlAttrs
  // Za useHead function-form (() => head) radi i u SSR-u i client-side.
  useHead(() => {
    const head: Record<string, any> = {}

    // htmlAttrs (npr. lang)
    const attrs = toValue(input.htmlAttrs)

    if (attrs && Object.keys(attrs).length > 0) {
      head.htmlAttrs = attrs
    }

    // canonical (default = url)
    const canonical = toCanonical(toValue(input.canonical) || toValue(input.url), toValue(input.keepQuery))

    if (canonical) {
      head.link = [{ rel: 'canonical', href: canonical }]
    }

    // JSON-LD script blokovi
    const lds = toValue(input.jsonLd)

    if (Array.isArray(lds) && lds.length > 0) {
      const scripts = lds
        .filter((ld): ld is Record<string, any> => Boolean(ld))
        .map(ld => ({
          type: 'application/ld+json',
          children: JSON.stringify(ld),
        }))

      if (scripts.length > 0) {
        head.script = scripts
      }
    }

    return head
  })
}